/**
 * ScoutingReportRowView - Displays a single scouting report row
 *
 * Renders a single row in the scouting reports list with a title line
 * (date + opponent) and subtitle (report type + author). Shows a green "Shared"
 * badge only for recipients (non-owners) to indicate someone shared this report with them.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { ChevronRight } from "lucide-react-native";
import React, { memo } from "react";

import type { ScoutingReport } from "@/features/player-detail/types";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for ScoutingReportRowView component
 */
interface ScoutingReportRowViewProps {
  /** The scouting report to display */
  readonly report: ScoutingReport;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether this is the last item in the list */
  readonly isLastItem: boolean;
  /** Callback when the row is pressed */
  readonly onPress: () => void;
  /** Whether the current user is the author of this report */
  readonly isOwner: boolean;
}

/**
 * Report item border class (static — semantic tokens auto-invert by theme)
 */
const REPORT_ITEM_CLASS = "border-b border-outline-subtle py-3";

/**
 * Report title text class (date + opponent on one line, static)
 */
const REPORT_TITLE_CLASS = "text-content-primary";

/**
 * Report subtitle text class (type + author, static)
 */
const REPORT_SUBTITLE_CLASS = "text-content-muted";

/**
 * ScoutingReportRowView renders a single scouting report row
 * @param props - View props containing report data and callbacks
 * @param props.report - The scouting report to display
 * @param props.isDark - Whether dark mode is active
 * @param props.isLastItem - Whether this is the last item in the list
 * @param props.onPress - Callback when the row is pressed
 * @param props.isOwner - Whether the current user is the author of this report
 * @returns Pressable row containing report details
 */
const ScoutingReportRowView = ({
  report,
  isLastItem,
  onPress,
  isOwner,
}: ScoutingReportRowViewProps) => (
  <Pressable
    onPress={onPress}
    className={isLastItem ? "py-3" : REPORT_ITEM_CLASS}
    // SE-6876: marks a RENDERED row, so a spec can assert rows exist rather
    // than inferring it from the card's heading. `testID`, not a
    // `data-testid` spread: the Pressable atom forwards props into RN-Web's
    // View, which renders `testID` as `data-testid` and silently DROPS an
    // unknown DOM prop. Measured — the spread form resolved 0 elements.
    testID={TEST_ID_PLAYER_DETAIL.SCOUTING_REPORT_ROW}
  >
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack className="flex-1 gap-1">
        <Text variant="body-strong" className={REPORT_TITLE_CLASS}>
          {report.date} {report.displayText}
        </Text>
        <Stack direction="horizontal" className="items-center gap-2">
          <Text variant="caption" className={REPORT_SUBTITLE_CLASS}>
            {report.reportType} &bull; by {isOwner ? "You" : report.author}
          </Text>
          {!isOwner && (
            <Box className="rounded-none bg-status-success-surface px-2 py-micro">
              <Text
                variant="unstyled"
                className="font-medium text-caption text-status-success"
              >
                Shared
              </Text>
            </Box>
          )}
        </Stack>
      </Stack>
      <Icon as={ChevronRight} size="sm" className="text-content-muted" />
    </Stack>
  </Pressable>
);

ScoutingReportRowView.displayName = "ScoutingReportRowView";

export default memo(ScoutingReportRowView);
